import { Box } from '@mui/material';
import { FC, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useVideoCall, VideoCallProvider } from '../../contexts/videoCall/context';
import { VideoCallActions } from '../../contexts/videoCall/types';
import appointmentsServiceClient from '../../lib/appointments/client';
import { getRoomUuidFromChannel } from '../../lib/format';
import MixpanelClient, { TrackingEventV2 } from '../../lib/tracking/mixpanel';
import AppointmentVideoCall from './components/AppointmentVideoCall';
import WaitingRoom from './components/WaitingRoom';
import styles from './Meeting.module.css';

const MeetingContent: FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const { videoCallState, dispatchVideoCall } = useVideoCall();

  const [queryParams] = useSearchParams();
  const source = queryParams.get('source') ?? 'unknown';

  // meeting info contain the chime video context
  const { meetingInfo } = videoCallState;

  useEffect(() => {
    (async () => {
      const meetingInformation = await appointmentsServiceClient.getMeetingInformation(token!);

      if (!meetingInformation) {
        navigate('/error');
        return;
      }

      const {
        appointment: appointmentInfo,
        host: hostInfo,
        currentParticipant,
        pusherChannel,
        participants,
      } = meetingInformation;

      const foundParticipant = participants.find((participant) => {
        return participant.uuid !== currentParticipant.uuid;
      });

      if (foundParticipant && token) {
        const videoPreview = {
          appointment: appointmentInfo,
          currentParticipant,
          attendant: foundParticipant,
          host: hostInfo,
          eventsChannel: pusherChannel,
          token,
          source,
        };

        MixpanelClient.trackEvent({
          eventName: TrackingEventV2.VideoCallLocalJoinWaitingRoom,
          properties: {
            source,
            appointmentId: appointmentInfo.coreAppointmentId,
            waitingRoomUuid: getRoomUuidFromChannel(pusherChannel),
            participantUuid: currentParticipant.uuid,
          },
        });

        dispatchVideoCall({ type: VideoCallActions.SetVideoPreview, payload: videoPreview });
      }
    })();
  }, [dispatchVideoCall, source, token]);

  return (
    <Box className={styles.meetingContainer}>
      <Helmet>
        <title>Enara: Join video call</title>
      </Helmet>

      {meetingInfo ? <AppointmentVideoCall /> : <WaitingRoom />}
    </Box>
  );
};

const Meeting: FC = () => (
  <VideoCallProvider>
    <MeetingContent />
  </VideoCallProvider>
);

export default Meeting;
